/**
 * O estado de uma partida, e como ele se transforma.
 *
 * Tudo aqui é imutável: uma ação não altera o estado, devolve outro. Isso custa
 * algumas cópias por turno e paga em três coisas que valem muito mais — desfazer
 * de graça, previsão sem efeito colateral, e um servidor que pode reexecutar a
 * partida inteira para conferir o que o cliente disse.
 */
import { Rng } from './rng.js';
import { covers, footprint, normalizeSize, SINGLE_TILE, } from './types.js';
export function isWeaponSlot(slot) {
    return slot?.weaponId !== undefined;
}
export function isItemSlot(slot) {
    return slot?.itemId !== undefined;
}
/** `w7` ou `i7` — o que o espaço é, sem confundir arma com item. */
export function slotKey(slot) {
    return slot.weaponId !== undefined ? `w${slot.weaponId}` : `i${slot.itemId}`;
}
/** A definição do que está no espaço, de arma ou de item. */
export function slotDef(content, slot) {
    if (!slot)
        return null;
    if (slot.weaponId !== undefined) {
        const def = content.weapons.get(slot.weaponId);
        return def ? { kind: 'arma', def } : null;
    }
    const def = content.items.get(slot.itemId);
    return def ? { kind: 'item', def } : null;
}
/**
 * O índice que a unidade empunha depois de a bolsa mudar.
 *
 * O preferido, se ali houver arma; senão a primeira arma da bolsa. Item não se
 * empunha — um antídoto na mão deixaria a unidade sem revide.
 */
export function settleEquipped(bag, preferred) {
    if (isWeaponSlot(bag[preferred]))
        return preferred;
    const first = bag.findIndex((slot) => isWeaponSlot(slot));
    return first >= 0 ? first : 0;
}
/** O baú que está nesta casa, se houver um ainda fechado. */
export function chestAt(state, at) {
    return state.chests?.find((c) => !c.opened && c.at.x === at.x && c.at.y === at.y);
}
/**
 * O que sai deste baú, já na forma de um espaço de bolsa.
 *
 * Devolve `null` quando o acervo não conhece mais o prêmio — o baú anotado com
 * uma arma que foi apagada. É a mesma pergunta que `slotDef` responde para a
 * bolsa, feita para o baú, e é aqui que as duas formas se resolvem numa só.
 */
export function chestPrize(content, chest) {
    if (chest.itemId !== undefined) {
        const def = content.items.get(chest.itemId);
        return def ? { slot: { itemId: def.id, usesLeft: def.uses }, name: def.name } : null;
    }
    if (chest.weaponId === undefined)
        return null;
    const def = content.weapons.get(chest.weaponId);
    return def ? { slot: { weaponId: def.id, usesLeft: def.uses }, name: def.name } : null;
}
export function unitById(state, id) {
    return state.units.find((u) => u.id === id);
}
/** As casas que esta unidade cobre. Uma só, no caso comum. */
export function unitTiles(unit) {
    return footprint(unit.position, unit.size);
}
/**
 * Quem está nesta casa — inclusive quem está com uma pata nela.
 *
 * Substitui a comparação direta com `position` em todo lugar onde o jogador
 * aponta para o tabuleiro: mirar a cauda de um dragão tem de selecionar o
 * dragão, e não o vazio.
 */
export function unitAt(state, p) {
    return state.units.find((u) => u.alive && covers(u.position, u.size, p));
}
/** O corpo da unidade, saneado. */
export function sizeOf(unit) {
    return normalizeSize(unit.size);
}
export function isBig(unit) {
    return normalizeSize(unit.size) !== SINGLE_TILE;
}
export function livingUnits(state, team) {
    return state.units.filter((u) => u.alive && (team === undefined || u.team === team));
}
/** A arma empunhada. Nula se o índice apontar para um item ou para o vazio. */
export function equippedWeapon(unit) {
    const slot = unit.inventory[unit.equipped];
    return isWeaponSlot(slot) ? slot : null;
}
export function weaponOf(content, unit) {
    const item = equippedWeapon(unit);
    if (!item || item.usesLeft <= 0)
        return null;
    return content.weapons.get(item.weaponId) ?? null;
}
/** Substitui uma unidade, devolvendo um estado novo. */
export function withUnit(state, unit) {
    return { ...state, units: state.units.map((u) => (u.id === unit.id ? unit : u)) };
}
export function withUnits(state, units) {
    const byId = new Map(units.map((u) => [u.id, u]));
    return { ...state, units: state.units.map((u) => byId.get(u.id) ?? u) };
}
/** Gerador na posição exata em que o estado o deixou. */
export function rngOf(state) {
    return Rng.fromState(state.rngState);
}
export function withRng(state, rng) {
    return { ...state, rngState: rng.state };
}
export function serialize(state) {
    return {
        v: state.formatVersion,
        seed: state.seed,
        rng: state.rngState,
        turn: state.turn,
        phase: state.phase,
        status: state.status,
        objective: state.objective,
        turnOrder: state.turnOrder,
        units: state.units,
        ...(state.chests !== undefined ? { chests: state.chests } : {}),
        ...(state.progression !== undefined ? { progression: state.progression } : {}),
    };
}
export function deserialize(data, map) {
    return {
        formatVersion: data.v,
        seed: data.seed,
        rngState: data.rng,
        turn: data.turn,
        phase: data.phase,
        map,
        units: data.units,
        objective: data.objective,
        status: data.status,
        turnOrder: data.turnOrder,
        ...(data.chests !== undefined ? { chests: data.chests } : {}),
        ...(data.progression !== undefined ? { progression: data.progression } : {}),
    };
}
//# sourceMappingURL=state.js.map